import React, { useEffect, useState } from "react";
import ProductManagementListing from "./ProductManagementListing";
import { columnTypes } from "src/components/UI/atoms/ATMTable/ATMTable";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "src/redux/store";
import {
  setIsOpenAddDialog,
  setIsOpenEditDialog,
  setPage,
  setRowsPerPage,
  setSearchValue,
  setIsTableLoading,
  setItems,
  setTotalItems,
} from "src/redux/slices/ProductManagementSlice";
import ATMMenu from "src/components/UI/atoms/ATMMenu/ATMMenu";
import AddProductManagementWrapper from "../Add/AddProductManagementWrapper";
import EditProductManagementFormWrapper from "../Edit/EditProductManagementWrapper";
import {
  useChangeStatusProductManagementByIdMutation,
  useCreateSynkProductManagementByAPIMutation,
  useDeleteProductManagementByIdMutation,
  useGetAllProductManagementDataQuery,
} from "src/services/ProductManagementService";
import { showConfirmationDialog } from "src/utils/showConfirmationDialog";
import { showToast } from "src/utils/showToaster";
import { usePermission } from "src/hooks/usePermission";
import { Tooltip } from "@mui/material";

type Props = {};

const ProductManagementListingWrapper = (props: Props) => {
  const dispatch = useDispatch();
  const {
    items,
    totalItems,
    page,
    rowsPerPage,
    searchValue,
    isOpenAddDialog,
    isOpenEditDialog,
    isTableLoading,
  } = useSelector((state: RootState) => state.productManagement);
  const [selectedProductManagementId, setSelectedProductManagementId] =
    useState<string>();

  const [deleteProductManagement] = useDeleteProductManagementByIdMutation();
  const [changeStatusProductManagement] =
    useChangeStatusProductManagementByIdMutation();
  const { data, isLoading, isFetching } = useGetAllProductManagementDataQuery({
    limit: rowsPerPage,
    searchValue: searchValue,
    params: ["title"],
    page: page,
    filterBy: [],
    dateFilter: {},
    orderBy: "createdAt",
    orderByValue: -1,
    isPaginationRequired: true,
  });
  const [triggerSync, { isLoading: isBookLoading }] =
    useCreateSynkProductManagementByAPIMutation();
  const handleAdd = () => {
    triggerSync("").then((res: any) => {
      if (res?.data.status) {
        showToast("success", res?.data.message);
      } else {
        showToast("error", res?.data.message);
      }
    });
  };
  useEffect(() => {
    if (isLoading || isFetching || isBookLoading) {
      dispatch(setIsTableLoading(true));
    } else {
      dispatch(setItems(data?.data || []));
      dispatch(setIsTableLoading(false));
      dispatch(setTotalItems(data?.totalItem || 0));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, isLoading, isFetching, isBookLoading]);

  const handleDeleteProductManagement = (row: string) => {
    showConfirmationDialog({
      title: "Hands Up",
      text: "Are you sure want to delete this Product Management ?",
      icon: "question",
      showCancelButton: true,
      next: (result) => {
        if (result?.isConfirmed) {
          deleteProductManagement(row).then((res: any) => {
            if (res.error) {
              showToast("error", res?.error?.data?.message);
            } else {
              showToast("success", res?.data?.message);
            }
          });
        }
      },
    });
  };

  const handleChangeStatusProductManagement = (row: string) => {
    showConfirmationDialog({
      title: "Hands Up",
      text: "Are you sure want to change status of this Advance Payment ?",
      icon: "question",
      showCancelButton: true,
      next: (result) => {
        if (result?.isConfirmed) {
          changeStatusProductManagement({ id: row }).then((res: any) => {
            if (res.error) {
              showToast("error", res?.error?.data?.message);
            } else {
              showToast("success", res?.data?.message);
            }
          });
        }
      },
    });
  };
  const moduleName = "PRODUCT";

  const canEdit = usePermission(moduleName, "UPDATE");
  const canDelete = usePermission(moduleName, "DELETE");
  const canActive = usePermission(moduleName, "ACTIVE/DEACTIVE");

  const columns: columnTypes[] = [
    { field: "title", headerName: "Title", flex: "flex-[1.5_1.5_0%]" },
    {
      field: "productAddedFrom",
      headerName: "Added From",
      flex: "flex-[1.5_1.5_0%]",
      align: "center",
      renderCell: (row: any) => (
        <Tooltip title={row?.productAddedFrom || "-"}>
          <div className="text-[13px]">
            {row?.productAddedFrom
              ? row.productAddedFrom.charAt(0).toUpperCase() +
                row.productAddedFrom.slice(1).toLowerCase()
              : "-"}
          </div>
        </Tooltip>
      ),
    },
    {
      field: "action",
      headerName: "Actions",
      flex: "flex-[1_1_0%]",
      align: "end",
      extraClasses: "",
      renderCell: (row: any) => {
        const options = [];

        if (canEdit) {
          options.push({
            label: "Edit",
            onClick: () => {
              dispatch(setIsOpenEditDialog(true));
              setSelectedProductManagementId(row?._id);
            },
          });
        }

        if (canActive) {
          options.push({
            label: "Active/Deactive",
            onClick: () => {
              handleChangeStatusProductManagement(row?._id);
            },
          });
        }

        if (canDelete) {
          options.push({
            label: "Delete",
            onClick: () => {
              handleDeleteProductManagement(row?._id);
            },
          });
        }

        return <ATMMenu options={options} />;
      },
    },
  ];

  return (
    <>
      {" "}
      <div>
        <ProductManagementListing
          columns={columns}
          syncProduct={handleAdd}
          rows={items}
          paginationProps={{
            isTableLoading,
            totalItems,
            page,
            rowsPerPage,
            searchValue,
            setIsOpenAddDialog: (value) => dispatch(setIsOpenAddDialog(value)),
            setPage: (newPage) => dispatch(setPage(newPage)),
            setRowsPerPage: (newRowsPerPage) =>
              dispatch(setRowsPerPage(newRowsPerPage)),
            setSearchValue: (newValue) => dispatch(setSearchValue(newValue)),
          }}
        />
      </div>
      {isOpenAddDialog ? (
        <AddProductManagementWrapper
          onClose={() => dispatch(setIsOpenAddDialog(false))}
        />
      ) : null}
      {isOpenEditDialog ? (
        <EditProductManagementFormWrapper
          onClose={() => dispatch(setIsOpenEditDialog(false))}
          selectedProductManagementId={selectedProductManagementId || ""}
        />
      ) : null}
    </>
  );
};

export default ProductManagementListingWrapper;
